import { createRoot } from "react-dom/client"
import "./style.css"
import { ServerProvider, setCurrentApp } from "@mimber/client"
import { ThemeProvider } from "@mimber/ui-web"
import { AuthProvider, FeatureToggleProvider } from "./core/contexts"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import { routeTree } from "./routeTree.gen"
import { ConfigGate } from "./components"
import { getAccessToken } from "@mimber/auth"
import { getSelectedOrganizationId } from "@/utils/organization"
import { configureAmplify } from "@/config/amplify"
import { initializeSentry } from "@/config/sentry"
import * as Sentry from "@sentry/react"

// Initialize app type for client guards
setCurrentApp("mimber-organization")

// Initialize monitoring
initializeSentry()

// Initialize Amplify configuration on app startup
configureAmplify()

// Create a router instance
const router = createRouter({
  routeTree,
  context: {
    // auth will initially be undefined - we'll pass it down from InnerApp
    auth: undefined!,
  },
})

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

const App = () => {
  const apiUrl =
    import.meta.env.VITE_MIMBER_ORG_API_URL || "http://localhost:3002"

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog>
      <ThemeProvider storageKey="mimber-organization-theme">
        <ServerProvider
          appAgent="MimberOrganization"
          customServerConfig={{ domain: apiUrl }}
          getAccessToken={async () => {
            const token = await getAccessToken()
            if (!token) {
              throw new Error("No access token available")
            }
            return token
          }}
          getOrganizationId={getSelectedOrganizationId}
        >
          <ConfigGate>
            <FeatureToggleProvider>
              <AuthProvider>
                <RouterProvider router={router} />
              </AuthProvider>
            </FeatureToggleProvider>
          </ConfigGate>
        </ServerProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  )
}

const ErrorFallback = () => {
  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      <h1>Something went wrong</h1>
      <p>We've been notified and are working on a fix.</p>
      <button onClick={() => window.location.reload()}>Reload page</button>
    </div>
  )
}

createRoot(document.getElementById("app")!).render(<App />)
